// Dossier documentaire, distinct du rapport technique limité pour l'affichage.
// L'adaptateur MIME est isolé ici : postal-mime est figé à 4.0.11. Son arbre permet de
// conserver aussi les alternatives et en-têtes de parties absents de l'Email synthétisé.
import PostalMime, { type Email, type Header } from "postal-mime";
import { recipientDetails } from "./recipients";

interface MimeNode {
  childNodes: MimeNode[];
  content: ArrayBuffer | null;
  headers: Header[];
  rawHeaderLines: { line: string }[];
  contentType: { parsed: { value: string; params: Record<string, string> } };
  contentDisposition: { parsed: { value: string; params: Record<string, string> } };
  contentTransferEncoding: { value: string };
  contentId?: string;
  getTextContent(): string;
}
export interface SourcePart {
  path: string;
  mimeType: string;
  parameters: Record<string, string>;
  disposition: string | null;
  dispositionParameters: Record<string, string>;
  transferEncoding: string;
  contentId: string | null;
  headers: Header[];
  headerLines: string[];
  size: number;
  sha256: string | null;
  firstBytesHex: string;
  text: string | null;
}
export interface SourceMessage {
  path: string;
  filename: string | null;
  size: number;
  sha256: string;
  rawHeaders: string;
  rawHeadersEncoding: "utf-8" | "latin-1";
  headers: Header[];
  headerLines: string[];
  identity: Pick<Email, "from" | "sender" | "replyTo" | "subject" | "messageId" | "inReplyTo" | "references" | "date" | "returnPath" | "deliveredTo">;
  recipients: ReturnType<typeof recipientDetails>;
  parts: SourcePart[];
  embeddedMessages: SourceMessage[];
}
export interface MailSource {
  version: 1;
  complete: boolean;
  message: SourceMessage | null;
  issues: string[];
  counts: { messages: number; headers: number; parts: number; textParts: number; bytes: number };
}

const MAX_MESSAGES = 100, MAX_DEPTH = 20, MAX_BYTES = 64 * 1024 * 1024;
const hex = (data: Uint8Array) => [...data].map(byte => byte.toString(16).padStart(2, "0")).join("");
export async function sourceHash(raw: ArrayBuffer): Promise<string> {
  return hex(new Uint8Array(await crypto.subtle.digest("SHA-256", raw)));
}

function rawHeaders(raw: ArrayBuffer): { rawHeaders: string; rawHeadersEncoding: "utf-8" | "latin-1" } {
  const bytes = new Uint8Array(raw);
  let end = bytes.length;
  for (let i = 0; i < bytes.length - 1; i++) {
    if (bytes[i] === 10 && bytes[i + 1] === 10) { end = i; break; }
    if (bytes[i] === 13 && bytes[i + 1] === 10 && bytes[i + 2] === 13 && bytes[i + 3] === 10) { end = i; break; }
  }
  const headerBytes = bytes.subarray(0, end);
  try { return { rawHeaders: new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(headerBytes), rawHeadersEncoding: "utf-8" }; }
  catch {
    // Conserver les octets d'un ancien en-tête non UTF-8 sans caractère de remplacement.
    let text = "";
    for (let i = 0; i < headerBytes.length; i += 0x8000) text += String.fromCharCode(...headerBytes.subarray(i, i + 0x8000));
    return { rawHeaders: text, rawHeadersEncoding: "latin-1" };
  }
}

export async function extractMailSource(raw: ArrayBuffer): Promise<MailSource> {
  const issues: string[] = [];
  const counts = { messages: 0, headers: 0, parts: 0, textParts: 0, bytes: 0 };
  const read = async (bytes: ArrayBuffer, path: string, filename: string | null, depth: number): Promise<SourceMessage | null> => {
    if (depth > MAX_DEPTH || counts.messages >= MAX_MESSAGES || counts.bytes + bytes.byteLength > MAX_BYTES) {
      issues.push(`Message ${path} : limite de décodage atteinte (profondeur, nombre ou volume). Son contenu n’est pas entièrement disponible.`);
      return null;
    }
    counts.messages++; counts.bytes += bytes.byteLength;
    try {
      const parser = new PostalMime({ forceRfc822Attachments: true }) as PostalMime & { root: MimeNode };
      const email = await parser.parse(bytes);
      const parts: SourcePart[] = [], embeddedMessages: SourceMessage[] = [];
      // Une évolution incompatible de postal-mime doit produire une erreur explicite,
      // jamais un dossier marqué complet auquel manqueraient des parties MIME.
      if (!parser.root || !Array.isArray(parser.root.childNodes)) throw new Error("Arbre MIME indisponible");
      const walk = async (node: MimeNode, partPath: string): Promise<void> => {
        counts.parts++; counts.headers += node.headers.length;
        const type = node.contentType.parsed.value;
        const body = node.content;
        const inlineText = type.startsWith("text/") && node.contentDisposition.parsed.value !== "attachment";
        const text = inlineText ? node.getTextContent() : null;
        if (text !== null) counts.textParts++;
        parts.push({ path: partPath, mimeType: type, parameters: node.contentType.parsed.params,
          disposition: node.contentDisposition.parsed.value || null, dispositionParameters: node.contentDisposition.parsed.params,
          transferEncoding: node.contentTransferEncoding.value, contentId: node.contentId ?? null,
          headers: node.headers, headerLines: node.rawHeaderLines.map(header => header.line), size: body?.byteLength ?? 0,
          sha256: body ? await sourceHash(body) : null, firstBytesHex: body ? hex(new Uint8Array(body).subarray(0, 16)) : "", text });
        const name = node.contentDisposition.parsed.params.filename || node.contentType.parsed.params.name || null;
        if (body && (type === "message/rfc822" || /\.(eml|rfc822)$/i.test(name ?? ""))) {
          const nested = await read(body, partPath + ".message", name, depth + 1);
          if (nested) embeddedMessages.push(nested);
        }
        for (let i = 0; i < node.childNodes.length; i++) await walk(node.childNodes[i], partPath + "." + (i + 1));
      };
      await walk(parser.root, path);
      return { path, filename, size: bytes.byteLength, sha256: await sourceHash(bytes), ...rawHeaders(bytes),
        headers: email.headers, headerLines: email.headerLines.map(header => header.line),
        identity: { from: email.from, sender: email.sender, replyTo: email.replyTo, subject: email.subject,
          messageId: email.messageId, inReplyTo: email.inReplyTo, references: email.references, date: email.date,
          returnPath: email.returnPath, deliveredTo: email.deliveredTo },
        recipients: recipientDetails(email), parts, embeddedMessages };
    } catch (error) {
      issues.push(`Message ${path} : impossible de décoder tous ses détails (${(error as Error).message}).`);
      return null;
    }
  };
  const message = await read(raw, "1", null, 0);
  return { version: 1, complete: Boolean(message) && issues.length === 0, message, issues, counts };
}
